import { type ConsentInfoResponse } from '@logto/schemas';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import type { ReactNode } from 'react';
import { useContext } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import BrandingHeader from '@/components/BrandingHeader';
import DynamicT from '@/shared/components/DynamicT';
import { getBrandingLogoUrl } from '@/shared/utils/logo';
import { layoutClassNames } from '@/utils/consts';

import { brandTaglineKey } from '../AppLayout/BrandPanel';
import FirstScreenLayout from '../FirstScreenLayout';

type ThirdPartyBranding = ConsentInfoResponse['application']['branding'];

type Props = {
  readonly children: ReactNode;
  readonly title: TFuncKey;
  readonly titleInterpolation?: Record<string, unknown>;
  readonly description?: TFuncKey;
  readonly thirdPartyBranding?: ThirdPartyBranding;
};

const LandingPageLayout = ({
  children,
  title,
  titleInterpolation,
  description,
  thirdPartyBranding,
}: Props) => {
  const { experienceSettings, theme } = useContext(PageContext);

  if (!experienceSettings) {
    return null;
  }

  const {
    color: { isDarkModeEnabled },
    branding,
  } = experienceSettings;

  return (
    <FirstScreenLayout pageMeta={{ titleKey: title, titleKeyInterpolation: titleInterpolation }}>
      <BrandingHeader
        className={classNames('page-header mb-6', layoutClassNames.brandingHeader)}
        headline={title}
        headlineInterpolation={titleInterpolation}
        description={
          description && (
            <span data-brand-tagline={description === brandTaglineKey || undefined}>
              <DynamicT forKey={description} />
            </span>
          )
        }
        logo={getBrandingLogoUrl({ theme, branding, isDarkModeEnabled })}
        thirdPartyLogo={
          thirdPartyBranding &&
          getBrandingLogoUrl({ theme, branding: thirdPartyBranding, isDarkModeEnabled })
        }
      />
      {children}
    </FirstScreenLayout>
  );
};

export default LandingPageLayout;
